import { Calculate } from "@mui/icons-material";
import AddLinkIcon from "@mui/icons-material/AddLink";
import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatQuoteIcon from "@mui/icons-material/FormatQuote";
import HorizontalRuleIcon from "@mui/icons-material/HorizontalRule";
import ImageIcon from "@mui/icons-material/Image";
import RedoIcon from "@mui/icons-material/Redo";
import UndoIcon from "@mui/icons-material/Undo";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Input,
  Stack,
  Typography,
} from "@mui/material";
import { Image } from "@tiptap/extension-image";
import { Link } from "@tiptap/extension-link";
import type { Node as ProseMirrorNode } from "@tiptap/pm/model";
import * as TiptapReact from "@tiptap/react";
import { StarterKit } from "@tiptap/starter-kit";
import React, { type ComponentProps, useEffect, useState } from "react";
import { joinStaticContentUrl } from "utils/staticContent/staticContent";

import { buildEditableContentBlockLinkHtml } from "./contentBlockHtml";
import InsertFormulaDialog from "./insertFormula";
import type { LinkTypeEnum } from "./insertLinkDlg";
import InsertLinkDialog from "./insertLinkDlg";
import awsImageFunctions from "../../helpers/awsImageFunctions";
import { getErrorMessage } from "../../utils/errorHandling";

type TiptapEditor = NonNullable<
  ComponentProps<typeof TiptapReact.EditorContent>["editor"]
>;
type TiptapChainedCommands = ReturnType<TiptapEditor["chain"]>;
interface TiptapNodeFactory {
  create(
    config: Record<string, unknown>,
  ): TiptapEditor["extensionManager"]["extensions"][number];
}

// `@tiptap/react` re-exports `Node` at runtime, but the workspace type surface doesn't
// expose it directly. Keep the adapter local so the editor can define custom nodes without
// relying on unstable deep imports into Tiptap internals.
const TiptapNode = (
  TiptapReact as typeof TiptapReact & {
    Node: TiptapNodeFactory;
  }
).Node;

export interface EditableContentBlockNotification {
  severity: "success" | "error";
  message: string;
}

interface FormulaCommandOptions {
  numberFormat: string;
  formula: string;
  abs?: boolean;
}

const CustomFormulaNode = TiptapNode.create({
  name: "formula",
  content: "text*",
  group: "inline",
  inline: true,
  selectable: true,
  draggable: true,
  atom: true,
  addAttributes() {
    return {
      "data-formula": {
        default: null,
      },
      "data-number-format": {
        default: null,
      },
      "data-abs": {
        default: false,
      },
    };
  },
  parseHTML() {
    return [
      {
        tag: "span[data-formula][data-number-format]",
      },
    ];
  },
  renderHTML({ node }: { node: ProseMirrorNode }) {
    const numberFormat = node.attrs["data-number-format"] as string | undefined;
    const previewText =
      numberFormat === "percent"
        ? "12%"
        : numberFormat === "text"
          ? "Example"
          : "12,345";

    return [
      "span",
      /* eslint-disable no-inline-colors/no-inline-colors -- Formula preview chips intentionally own a fixed inline style so the editor preview stays self-contained in ProseMirror HTML. */
      {
        "data-formula": node.attrs["data-formula"],
        "data-number-format": node.attrs["data-number-format"],
        "data-abs": node.attrs["data-abs"] ?? false,
        style:
          "display: inline-block; padding: 0 6px; border-radius: 12px; background: rgba(12, 1, 64, 0.08); border: 1px dashed rgba(12, 1, 64, 0.5); cursor: pointer; font-size: 0.9em;",
      },
      /* eslint-enable no-inline-colors/no-inline-colors */
      `ƒ ${previewText}`,
    ];
  },
  addCommands() {
    return {
      setFormula:
        (options: FormulaCommandOptions) =>
        ({ commands }: { commands: TiptapChainedCommands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: {
              "data-formula": options.formula,
              "data-number-format": options.numberFormat,
              "data-abs": options.abs ?? false,
            },
          });
        },
    };
  },
});

export const editableContentBlockEditorExtensions = [
  CustomFormulaNode,
  Image,
  Link.configure({
    protocols: ["mailto"],
    linkOnPaste: false,
    autolink: false,
  }),
  StarterKit.configure({
    bulletList: {
      keepMarks: true,
      keepAttributes: false,
    },
    orderedList: {
      keepMarks: true,
      keepAttributes: false,
    },
    heading: {
      levels: [2, 3, 4, 5],
    },
    horizontalRule: {},
    code: false,
    codeBlock: false,
    italic: false,
    // StarterKit already bundles Link in this Tiptap version, so disable
    // that copy and keep the explicitly configured version above to avoid
    // duplicate-extension warnings in the editor.
    link: false,
    underline: false,
  }),
];

const handleMenuClick = (
  editor: TiptapEditor,
  command: (c: TiptapChainedCommands) => TiptapChainedCommands,
) => {
  if (editor.state.selection.empty) {
    command(editor.chain().focus().selectParentNode()).run();
  } else {
    command(editor.chain().focus()).run();
  }
};

export const EditableContentBlockMenuBar = ({
  editor,
  filePrefix,
  selectedNode,
  onNotify,
}: {
  editor: TiptapEditor;
  filePrefix: string;
  selectedNode?: ProseMirrorNode | null;
  onNotify: (notification: EditableContentBlockNotification) => void;
}) => {
  const [showImgDlg, setShowImgDlg] = useState(false);
  const [showLinkDlg, setShowLinkDlg] = useState(false);
  const [showFormulaDlg, setShowFormulaDlg] = useState(false);
  const selectedFormulaNode =
    selectedNode?.type.name === "formula" ? selectedNode : null;

  useEffect(() => {
    if (selectedFormulaNode) {
      setShowFormulaDlg(true);
    }
  }, [selectedFormulaNode]);

  const [file, setFile] = useState<File>();

  const handleOpenImageDialog = () => {
    setShowImgDlg(true);
  };

  const handleOpenLinkDialog = () => {
    setShowLinkDlg(true);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      setFile(e.target.files[0]);
    }
  };

  const handleInsertImage = async () => {
    if (!file) {
      setShowImgDlg(false);
      return;
    }

    try {
      const imageKey = await awsImageFunctions.uploadImageToAws(
        file,
        `${filePrefix}-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[-:]/g, "")}`,
      );

      editor
        .chain()
        .focus()
        .setImage({ src: joinStaticContentUrl(imageKey) })
        .run();
    } catch (error) {
      onNotify({
        severity: "error",
        message: getErrorMessage(error),
      });
    } finally {
      setFile(undefined);
      setShowImgDlg(false);
    }
  };

  const handleCloseIngDlg = () => {
    setFile(undefined);
    setShowImgDlg(false);
  };

  const handleLinkInsert = (
    linkType: LinkTypeEnum,
    url: string,
    displayText: string | undefined,
  ) => {
    editor
      .chain()
      .focus()
      .insertContent(
        buildEditableContentBlockLinkHtml(linkType, url, displayText),
      )
      .run();
    setShowLinkDlg(false);
  };

  const handleFormulaInsert = (
    formula: string,
    numberFormat: string,
    abs?: boolean,
  ) => {
    const formulaData = { numberFormat, formula };
    if (abs !== undefined) {
      Object.assign(formulaData, { abs });
    }
    editor.chain().focus().setFormula(formulaData).run();
    setShowFormulaDlg(false);
  };

  return (
    <>
      <Dialog open={showImgDlg}>
        <DialogTitle>Insert Image</DialogTitle>
        <ul>
          <li>
            Resize your image in a photo editor first to suit your need. Images
            will scale down to fit the modal, but won&apos;t scale up.
          </li>
          <li>
            The maximum file size is 1MB, try saving as a JPEG with compression
            if your image is too large.
          </li>
        </ul>
        <DialogContent>
          <Input
            type="file"
            inputProps={{ accept: "image/*" }}
            onChange={handleFileChange}
          />
        </DialogContent>
        <Stack direction="row" spacing={1}>
          <IconButton
            aria-label="Cancel image insert"
            color="error"
            onClick={handleCloseIngDlg}
          >
            <CancelIcon />
            Cancel
          </IconButton>
          <IconButton
            aria-label="Insert selected image"
            color="success"
            onClick={handleInsertImage}
            disabled={!file}
          >
            <CheckCircleIcon />
            Insert
          </IconButton>
        </Stack>
      </Dialog>
      <InsertLinkDialog
        open={showLinkDlg}
        onCancel={() => setShowLinkDlg(false)}
        onInsert={handleLinkInsert}
      />
      <InsertFormulaDialog
        open={showFormulaDlg}
        initialValues={{
          formula: selectedFormulaNode?.attrs["data-formula"] ?? "",
          numberFormat:
            selectedFormulaNode?.attrs["data-number-format"] ?? "currency",
          abs: Boolean(selectedFormulaNode?.attrs["data-abs"]),
        }}
        onCancel={() => setShowFormulaDlg(false)}
        onInsert={({ formula, numberFormat, abs }) =>
          handleFormulaInsert(formula, numberFormat, abs)
        }
      />
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
        <IconButton
          aria-label="Bold"
          onClick={() => handleMenuClick(editor, (c) => c.toggleBold())}
          className="active"
        >
          <FormatBoldIcon />
        </IconButton>
        <IconButton
          aria-label="Heading 2"
          onClick={() =>
            handleMenuClick(editor, (c) => c.toggleHeading({ level: 2 }))
          }
        >
          <Typography variant="body1">H2</Typography>
        </IconButton>
        <IconButton
          aria-label="Heading 3"
          onClick={() =>
            handleMenuClick(editor, (c) => c.toggleHeading({ level: 3 }))
          }
        >
          <Typography variant="body1">H3</Typography>
        </IconButton>
        <IconButton
          aria-label="Heading 4"
          onClick={() =>
            handleMenuClick(editor, (c) => c.toggleHeading({ level: 4 }))
          }
        >
          <Typography variant="body1">H4</Typography>
        </IconButton>
        <IconButton
          aria-label="Heading 5"
          onClick={() =>
            handleMenuClick(editor, (c) => c.toggleHeading({ level: 5 }))
          }
        >
          <Typography variant="body1">H5</Typography>
        </IconButton>
        <IconButton
          aria-label="Paragraph"
          onClick={() =>
            handleMenuClick(editor, (c) => c.unsetAllMarks().setParagraph())
          }
        >
          <Typography variant="body1">Paragraph</Typography>
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton
          aria-label="Bulleted list"
          onClick={() => handleMenuClick(editor, (c) => c.toggleBulletList())}
        >
          <FormatListBulletedIcon />
        </IconButton>
        <IconButton
          aria-label="Numbered list"
          onClick={() => handleMenuClick(editor, (c) => c.toggleOrderedList())}
        >
          <FormatListNumberedIcon />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton
          aria-label="Block quote"
          onClick={() => handleMenuClick(editor, (c) => c.toggleBlockquote())}
        >
          <FormatQuoteIcon />
        </IconButton>
        <IconButton
          aria-label="Horizontal rule"
          onClick={() => handleMenuClick(editor, (c) => c.setHorizontalRule())}
        >
          <HorizontalRuleIcon />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton aria-label="Insert image" onClick={handleOpenImageDialog}>
          <ImageIcon />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton
          aria-label="Undo"
          onClick={() => handleMenuClick(editor, (c) => c.undo())}
        >
          <UndoIcon />
        </IconButton>
        <IconButton
          aria-label="Redo"
          onClick={() => handleMenuClick(editor, (c) => c.redo())}
        >
          <RedoIcon />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton aria-label="Insert link" onClick={handleOpenLinkDialog}>
          <AddLinkIcon />
        </IconButton>
        <Divider orientation="vertical" flexItem />
        <IconButton
          aria-label="Insert formula"
          onClick={() => setShowFormulaDlg(true)}
        >
          <Typography variant="body1">
            <Calculate />
          </Typography>
        </IconButton>
      </Stack>
    </>
  );
};
